/**
 * Client wrapper for the email verification-code callables (see
 * functions/auth/emailVerification.js). Used by the registration flow to verify
 * an email by 6-digit code *before* the account is created.
 *
 * Each callable throws a Firebase HttpsError on failure; we surface its `message`
 * (which the backend sets to a user-friendly string) so callers can show it directly.
 */
import { functions } from '../FirebaseIndex';
import { httpsCallable } from 'firebase/functions';

const friendlyError = (error, fallback) => {
  const message = error && error.message ? error.message : fallback;
  const err = new Error(message);
  err.code = error && error.code;
  return err;
};

/**
 * Step 1 — email a fresh 6-digit code to the address.
 * @param {string} email
 * @returns {Promise<{success:boolean, expiresInSeconds:number, cooldownSeconds:number}>}
 */
export const requestEmailVerificationCode = async (email) => {
  try {
    const fn = httpsCallable(functions, 'requestEmailVerificationCode');
    const result = await fn({ email });
    return result.data;
  } catch (error) {
    throw friendlyError(error, 'Could not send the verification code. Please try again.');
  }
};

/**
 * Step 2 — validate the code the user entered.
 * @param {string} email
 * @param {string} code
 * @returns {Promise<{success:boolean, alreadyVerified?:boolean}>}
 */
export const confirmEmailVerificationCode = async (email, code) => {
  try {
    const fn = httpsCallable(functions, 'confirmEmailVerificationCode');
    const result = await fn({ email, code });
    return result.data;
  } catch (error) {
    throw friendlyError(error, 'Could not verify the code. Please try again.');
  }
};

/**
 * Step 3 — after the account is created, flip the auth user's emailVerified flag.
 * Must be called while the newly-created user is signed in.
 * @returns {Promise<{success:boolean}>}
 */
export const finalizeVerifiedSignup = async () => {
  try {
    const fn = httpsCallable(functions, 'finalizeVerifiedSignup');
    const result = await fn();
    return result.data;
  } catch (error) {
    throw friendlyError(error, 'Could not finalize verification. Please try again.');
  }
};
